import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { cn } from '@reflex/ui/lib/utils';
import { AddFilterButton } from './AddFilterButton';
import { FilterChip } from './FilterChip';
import { FilterSearchInput } from './FilterSearchInput';
import { FilterToggleButton } from './FilterToggleButton';
import { isFilterApplied, type FilterDefinition } from './filter-types';

export interface FilterBarSearch {
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  ariaLabel: string;
}

interface FilterBarProps {
  /** Every facet the list can be narrowed by, applied or not. */
  filters: FilterDefinition[];
  /** Resets every filter. The × on the toggle only renders when provided. */
  onClear?: () => void;
  /** Renders a search box left of the toggle, with `@` wired to these facets. */
  search?: FilterBarSearch;
  className?: string;
}

/**
 * A search box, a Filters toggle, and a drawer of the applied filters.
 *
 * The toolbar line stays one line: search, then the toggle, always in that
 * order and always present. Everything else lives in a drawer underneath, so
 * the chips can spell out the whole query without permanently spending two
 * rows of a narrow sidebar on it. The drawer opens on the toggle and whenever
 * a filter is added from the search box (there is no point applying a filter
 * you cannot see), and closes as soon as attention moves to the list itself.
 */
export function FilterBar({ filters, onClear, search, className }: FilterBarProps) {
  const drawerId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const applied = filters.filter(isFilterApplied);

  // Attention moving to the list is the end of the filtering task, so the
  // drawer gets out of the way. Menus portal to the body, so a click inside one
  // is still a click inside the bar as far as this is concerned.
  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (rootRef.current?.contains(target)) return;
      if (target.closest('[data-radix-popper-content-wrapper]')) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [open]);

  // Removing a chip takes the focused element with it. The add button is where
  // that filter went, so send focus there — after the commit, since the button
  // may only exist as a result of this removal.
  const focusAddButton = useCallback(() => {
    requestAnimationFrame(() => addButtonRef.current?.focus());
  }, []);

  if (filters.length === 0 && !search) return null;

  return (
    <div ref={rootRef} className={cn('flex flex-col gap-1.5', className)}>
      <div className="flex items-center gap-1.5">
        {search && (
          <FilterSearchInput
            filters={filters}
            value={search.value}
            onValueChange={search.onValueChange}
            placeholder={search.placeholder}
            aria-label={search.ariaLabel}
            className="min-w-0 flex-1"
            // Applying a filter from the search box opens the drawer: the chip
            // is the receipt for what just happened to the list.
            onFilterApplied={() => setOpen(true)}
          />
        )}
        {filters.length > 0 && (
          <FilterToggleButton
            activeCount={applied.length}
            open={open}
            onToggle={() => setOpen((previous) => !previous)}
            onClear={onClear}
            controls={drawerId}
          />
        )}
      </div>

      {open && filters.length > 0 && (
        <div
          id={drawerId}
          role="group"
          aria-label="Filters"
          data-testid="filter-bar-drawer"
          className="flex flex-wrap items-center gap-1.5 duration-150 animate-in fade-in-0 slide-in-from-top-1"
        >
          {applied.map((definition) => (
            <FilterChip key={definition.id} definition={definition} onRemoved={focusAddButton} />
          ))}
          <AddFilterButton
            ref={addButtonRef}
            filters={filters}
            onClear={onClear}
            compact={applied.length > 0}
          />
        </div>
      )}
    </div>
  );
}
